import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Brand } from "../components/Brand";
import { ChildControlsForm } from "../parent/ChildControlsForm";
import { ForgotPinRecovery, PARENT_RESET_CONFIRMATION } from "../parent/ForgotPinRecovery";
import { MAX_PIN_ATTEMPTS } from "../parent/AttemptController";
import { controlsForPreset, identifyPreset } from "../parent/parentDefaults";
import { useParentMode } from "../parent/ParentModeContext";
import type { ChildControls, ParentPinRecord } from "../parent/parentTypes";
import { validateChildControls } from "../parent/parentValidation";
import { createPinRecord, isValidPin } from "../parent/pinCrypto";
import type { PerfectDrawProject } from "../projects/types";
import { listProjects } from "../storage/db";

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function PinFields({
  pin,
  confirmation,
  onPin,
  onConfirmation,
  prefix,
}: {
  pin: string;
  confirmation: string;
  onPin: (value: string) => void;
  onConfirmation: (value: string) => void;
  prefix: string;
}) {
  return (
    <>
      <label className="form-field" htmlFor={`${prefix}-pin`}>
        <span>New PIN</span>
        <input
          autoComplete="new-password"
          id={`${prefix}-pin`}
          inputMode="numeric"
          maxLength={6}
          onChange={(event) => onPin(event.target.value.replace(/\D/g, "").slice(0, 6))}
          pattern="[0-9]{4,6}"
          required
          type="password"
          value={pin}
        />
        <small>Use 4 to 6 digits.</small>
      </label>
      <label className="form-field" htmlFor={`${prefix}-confirm`}>
        <span>Confirm new PIN</span>
        <input
          autoComplete="new-password"
          id={`${prefix}-confirm`}
          inputMode="numeric"
          maxLength={6}
          onChange={(event) => onConfirmation(event.target.value.replace(/\D/g, "").slice(0, 6))}
          pattern="[0-9]{4,6}"
          required
          type="password"
          value={confirmation}
        />
      </label>
    </>
  );
}

function SetupScreen() {
  const parent = useParentMode();
  const [pin, setPin] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [pinRecord, setPinRecord] = useState<ParentPinRecord | null>(null);
  const [controls, setControls] = useState<ChildControls>(() => controlsForPreset("recommended"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handlePin = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError("");
    if (!isValidPin(pin)) {
      setError("Choose a PIN with 4 to 6 digits.");
      setPin("");
      setConfirmation("");
      return;
    }
    if (pin !== confirmation) {
      setError("Those PINs do not match. Please try both again.");
      setPin("");
      setConfirmation("");
      return;
    }
    if (!acknowledged) {
      setError("Please acknowledge the local PIN recovery note.");
      return;
    }
    setBusy(true);
    try {
      setPinRecord(await createPinRecord(pin));
      setPin("");
      setConfirmation("");
    } catch {
      setError("The PIN could not be prepared on this device. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!pinRecord) {
    return (
      <section className="parent-card" aria-labelledby="parent-setup-title">
        <p className="eyebrow">First-time setup</p>
        <h1 id="parent-setup-title">Create a Parent Mode PIN</h1>
        <p>This child-resistant gate protects settings on this browser and device.</p>
        <form onSubmit={(event) => void handlePin(event)}>
          <PinFields
            confirmation={confirmation}
            onConfirmation={setConfirmation}
            onPin={setPin}
            pin={pin}
            prefix="setup"
          />
          <label className="parent-acknowledgement">
            <input
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              type="checkbox"
            />
            <span>
              I understand that a forgotten PIN requires resetting only the local Parent Mode
              configuration. Drawings and comics are not removed.
            </span>
          </label>
          {error ? <p role="alert">{error}</p> : null}
          <button className="button button--primary button--large" disabled={busy} type="submit">
            {busy ? "Preparing…" : "Continue to Child Controls"}
          </button>
        </form>
      </section>
    );
  }

  const save = (): void => {
    setError("");
    try {
      parent.saveInitialConfiguration(pinRecord, validateChildControls(controls));
    } catch (saveError) {
      setError(
        saveError instanceof Error && /creation path/i.test(saveError.message)
          ? "Keep at least one of drawings, comics or activities available."
          : "Parent Mode could not be saved. Your existing projects are unchanged.",
      );
    }
  };

  return (
    <section className="parent-card parent-card--wide" aria-labelledby="parent-controls-title">
      <p className="eyebrow">Choose a starting point</p>
      <h1 id="parent-controls-title">Set up Child Mode</h1>
      <p>Choose a preset, then adjust any setting before saving.</p>
      <div className="parent-preset-actions">
        <button onClick={() => setControls(controlsForPreset("recommended"))} type="button">
          Recommended Child Mode
        </button>
        <button onClick={() => setControls(controlsForPreset("full"))} type="button">
          Full Access
        </button>
      </div>
      <ChildControlsForm controls={controls} onChange={setControls} />
      {error ? <p role="alert">{error}</p> : null}
      <button className="button button--primary button--large" onClick={save} type="button">
        Save Parent Mode
      </button>
    </section>
  );
}

function UnlockScreen() {
  const parent = useParentMode();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    Math.ceil(parent.getAttemptStatus().remainingMs / 1000),
  );

  useEffect(() => {
    if (remainingSeconds <= 0) return;
    const timer = window.setTimeout(
      () => setRemainingSeconds(Math.ceil(parent.getAttemptStatus().remainingMs / 1000)),
      250,
    );
    return () => window.clearTimeout(timer);
  }, [parent, remainingSeconds]);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy || remainingSeconds > 0) return;
    setBusy(true);
    setError("");
    try {
      const result = await parent.unlock(pin);
      if (result.kind === "incorrect") {
        const failures = parent.getAttemptStatus().failures;
        setError(
          `That PIN did not unlock Parent Mode. ${MAX_PIN_ATTEMPTS - failures} tries remain.`,
        );
      } else if (result.kind === "cooldown") {
        setRemainingSeconds(Math.ceil(parent.getAttemptStatus().remainingMs / 1000));
        setError("Please wait before trying Parent Mode again.");
      } else if (result.kind !== "success") {
        setError("Parent Mode cannot be unlocked until its local settings are repaired.");
      }
    } catch {
      setError("Parent Mode could not check that PIN. Please try again.");
    } finally {
      setPin("");
      setBusy(false);
    }
  };

  return (
    <section className="parent-card" aria-labelledby="parent-unlock-title">
      <p className="eyebrow">For parents and carers</p>
      <h1 id="parent-unlock-title">Unlock Parent Mode</h1>
      <p>Enter the local PIN to change Child Mode controls.</p>
      <form onSubmit={(event) => void submit(event)}>
        <label className="form-field" htmlFor="unlock-pin">
          <span>Parent Mode PIN</span>
          <input
            autoComplete="current-password"
            autoFocus
            disabled={remainingSeconds > 0}
            id="unlock-pin"
            inputMode="numeric"
            maxLength={6}
            onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 6))}
            required
            type="password"
            value={pin}
          />
        </label>
        {remainingSeconds > 0 ? (
          <p aria-live="polite" className="parent-cooldown">
            Please wait {remainingSeconds} seconds before trying again.
          </p>
        ) : null}
        {error ? <p role="alert">{error}</p> : null}
        <button
          className="button button--primary button--large"
          disabled={busy || remainingSeconds > 0}
          type="submit"
        >
          {busy ? "Checking…" : "Unlock Parent Mode"}
        </button>
      </form>
      <ForgotPinRecovery onReset={parent.resetConfiguration} />
      <p className="parent-security-note">
        This is a child-resistant local gate, not high-security authentication or a replacement for
        device parental controls.
      </p>
    </section>
  );
}

function CorruptScreen() {
  const parent = useParentMode();
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const reset = (): void => {
    try {
      parent.resetConfiguration(confirmation);
    } catch {
      setError(
        `Type ${PARENT_RESET_CONFIRMATION} exactly to remove only the Parent Mode configuration.`,
      );
    }
  };
  return (
    <section className="parent-card" aria-labelledby="parent-recovery-title">
      <p className="eyebrow">Local settings need help</p>
      <h1 id="parent-recovery-title">Recover Parent Mode</h1>
      <div className="notice notice--error" role="alert">
        The Parent Mode record is damaged. Recommended Child Mode restrictions are active.
      </div>
      <p>
        Resetting here removes only the Parent Mode PIN and child controls. It never deletes
        drawings, comics, artwork or backup files.
      </p>
      <label className="form-field" htmlFor="parent-reset-confirmation">
        <span>Type {PARENT_RESET_CONFIRMATION} to confirm</span>
        <input
          autoComplete="off"
          id="parent-reset-confirmation"
          onChange={(event) => setConfirmation(event.target.value)}
          value={confirmation}
        />
      </label>
      {error ? <p role="alert">{error}</p> : null}
      <button className="button button--danger" onClick={reset} type="button">
        Reset Parent Mode configuration
      </button>
    </section>
  );
}

function LocalOverview() {
  const [projects, setProjects] = useState<PerfectDrawProject[]>([]);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    void listProjects()
      .then((items) => {
        if (active) setProjects(items);
      })
      .catch(() => {
        if (active) setError("The local project overview is unavailable right now.");
      });
    return () => {
      active = false;
    };
  }, []);
  const drawings = projects.filter((project) => project.type === "drawing").length;
  const comics = projects.length - drawings;
  const latest = projects
    .map((project) => new Date(project.updatedAt))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((left, right) => right.getTime() - left.getTime())[0];
  return (
    <section className="parent-overview" aria-label="Local project overview">
      <h2>Projects on this device</h2>
      {error ? <p role="status">{error}</p> : null}
      <dl>
        <div>
          <dt>Total projects</dt>
          <dd>{projects.length}</dd>
        </div>
        <div>
          <dt>Drawings</dt>
          <dd>{drawings}</dd>
        </div>
        <div>
          <dt>Comics</dt>
          <dd>{comics}</dd>
        </div>
        <div>
          <dt>Most recently edited</dt>
          <dd>{latest ? DATE_FORMATTER.format(latest) : "No projects yet"}</dd>
        </div>
      </dl>
      <p>No screen time or activity history is collected.</p>
    </section>
  );
}

function SettingsScreen() {
  const parent = useParentMode();
  const [controls, setControls] = useState<ChildControls>(() => ({ ...parent.controls }));
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [pin, setPin] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const preset = useMemo(() => identifyPreset(controls), [controls]);

  const saveControls = (): void => {
    setError("");
    setStatus("");
    try {
      parent.saveControls(validateChildControls(controls));
      setStatus("Child controls saved on this device.");
    } catch (saveError) {
      setError(
        saveError instanceof Error && /creation path/i.test(saveError.message)
          ? "Keep at least one of drawings, comics or activities available."
          : "Controls could not be saved. The previous settings remain active.",
      );
    }
  };

  const changePin = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError("");
    setStatus("");
    if (!isValidPin(pin) || pin !== confirmation) {
      setError(
        !isValidPin(pin) ? "Choose a new PIN with 4 to 6 digits." : "Those new PINs do not match.",
      );
      setPin("");
      setConfirmation("");
      return;
    }
    setPinBusy(true);
    try {
      parent.saveChangedPin(await createPinRecord(pin));
      setStatus("The Parent Mode PIN was changed.");
    } catch {
      setError("The PIN could not be changed. The previous PIN still works.");
    } finally {
      setPin("");
      setConfirmation("");
      setPinBusy(false);
    }
  };

  return (
    <div className="parent-settings-layout">
      <section className="parent-card parent-card--wide" aria-labelledby="parent-settings-title">
        <div className="parent-active-heading">
          <div>
            <p className="eyebrow">Parent Mode Active</p>
            <h1 id="parent-settings-title">Child Mode controls</h1>
            <p>
              Preset:{" "}
              {preset === "recommended"
                ? "Recommended"
                : preset === "full"
                  ? "Full Access"
                  : "Custom"}
            </p>
          </div>
          <button className="button button--secondary" onClick={parent.lock} type="button">
            Return to Child Mode
          </button>
        </div>
        <div className="parent-preset-actions">
          <button onClick={() => setControls(controlsForPreset("recommended"))} type="button">
            Reset to Recommended
          </button>
          <button onClick={() => setControls(controlsForPreset("full"))} type="button">
            Reset to Full Access
          </button>
        </div>
        <ChildControlsForm controls={controls} onChange={setControls} />
        {error ? <p role="alert">{error}</p> : null}
        {status ? (
          <p aria-live="polite" role="status">
            {status}
          </p>
        ) : null}
        <button
          className="button button--primary button--large"
          onClick={saveControls}
          type="button"
        >
          Save Controls
        </button>
      </section>

      <aside className="parent-settings-side">
        <LocalOverview />
        <section className="parent-card" aria-labelledby="change-pin-title">
          <h2 id="change-pin-title">Change PIN</h2>
          <p>A fresh random salt will replace the previous derived PIN data.</p>
          <form onSubmit={(event) => void changePin(event)}>
            <PinFields
              confirmation={confirmation}
              onConfirmation={setConfirmation}
              onPin={setPin}
              pin={pin}
              prefix="change"
            />
            <button className="button button--secondary" disabled={pinBusy} type="submit">
              {pinBusy ? "Changing…" : "Change PIN"}
            </button>
          </form>
        </section>
        <section className="parent-card parent-privacy">
          <h2>Local and private</h2>
          <p>
            The PIN hash and controls stay in this browser. They are not accounts, are not uploaded,
            and are not included in project backups.
          </p>
          <p>
            Clearing browser site data removes Parent Mode as well as local app data. Export project
            backups before clearing site data when artwork must be retained.
          </p>
        </section>
      </aside>
    </div>
  );
}

export default function ParentScreen() {
  const parent = useParentMode();
  return (
    <main className="parent-screen">
      <header className="simple-header parent-header">
        <Brand compact />
        <Link className="button button--secondary button--small" to="/">
          ← My projects
        </Link>
      </header>
      {parent.storageState.kind === "corrupt" ? (
        <CorruptScreen />
      ) : parent.storageState.kind === "unconfigured" ? (
        <SetupScreen />
      ) : parent.isUnlocked ? (
        <SettingsScreen />
      ) : (
        <UnlockScreen />
      )}
    </main>
  );
}
